import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, doc, getDocs, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { AdminApiClient } from '../../services/adminApiClient';
import { 
  FileText, 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  Eye, 
  CheckCircle, 
  Clock, 
  Save, 
  ExternalLink,
  Shield,
  HelpCircle,
  BookOpen,
  Layers,
  Smartphone,
  Monitor,
  X,
  AlertTriangle
} from 'lucide-react';

export interface DynamicPageSection {
  id: string;
  heading: string;
  body: string;
  bulletPoints?: string[];
  callout?: string;
}

export interface DynamicPageItem {
  id: string;
  slug: string;
  title: string;
  category: 'Need Help' | 'About ReUseIt' | 'Legal & Policies';
  subtitle: string;
  status: 'published' | 'draft';
  lastUpdated: string;
  sections: DynamicPageSection[];
}

export const DynamicPagesManager: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');

  // Pre-populated pages matching Flutter platform info topics
  const [pages, setPages] = useState<DynamicPageItem[]>([
    {
      id: 'page_how_it_works',
      slug: '/how-it-works',
      title: 'How ReUseIt Escrow Works',
      category: 'Need Help',
      subtitle: '100% Protection for Buyers and Sellers Across Nigeria',
      status: 'published',
      lastUpdated: 'Sep 20, 2026',
      sections: [
        {
          id: 's1',
          heading: '1. Browse & Reserve with Escrow Lock',
          body: 'When you purchase an item, funds are locked inside Flutterwave tier-1 regulated banking escrow custody.',
          callout: 'Sellers never receive payment until the buyer inspects and approves the item.'
        },
        {
          id: 's2',
          heading: '2. 48-Hour Inspection Window',
          body: 'Once delivered, the buyer has a full 48-hour inspection window to test electronics, inspect condition, and verify serial numbers.',
          bulletPoints: [
            'Test battery health, screen, and ports',
            'Verify device is unlinked from iCloud / Google accounts',
            'File dispute to freeze funds if defective'
          ]
        },
        {
          id: 's3',
          heading: '3. Dual Confirmation & Instant Settlement',
          body: 'Upon release, payment settles instantly to the vendor\'s wallet with automatic NIBSS Nigerian bank transfer capability.'
        }
      ]
    },
    {
      id: 'page_disputes',
      slug: '/disputes',
      title: 'Dispute Resolution & Refunds',
      category: 'Need Help',
      subtitle: 'Fair, Transparent & Certified Human Mediation within 24 Hours',
      status: 'published',
      lastUpdated: 'Sep 19, 2026',
      sections: [
        {
          id: 's1',
          heading: 'When to File an Escrow Dispute',
          body: 'File a dispute if items are defective, misdescribed, counterfeit, or if the seller fails to dispatch within 5 business days.',
          bulletPoints: [
            'Transit damage or missing accessories',
            'Cloud-locked or blacklisted phones',
            'Significant cosmetic divergence from photos'
          ]
        },
        {
          id: 's2',
          heading: '24-Hour Certified Mediation',
          body: 'ReUseIt human dispute specialists examine photos, video tests, and chat logs to issue refunds or return authorizations within 24 hours.'
        }
      ]
    },
    {
      id: 'page_safety',
      slug: '/safety',
      title: 'Safe Trading Guidelines',
      category: 'Need Help',
      subtitle: 'Essential Rules to Trade Confidently and Avoid Fraud in Nigeria',
      status: 'published',
      lastUpdated: 'Sep 18, 2026',
      sections: [
        {
          id: 's1',
          heading: 'Never Pay Outside ReUseIt Escrow',
          body: 'Scammers frequently request direct bank transfers or cash off-platform. Paying off-platform voids all escrow guarantees and transit insurance.',
          callout: 'Always keep chat and payment inside the official ReUseIt application.'
        },
        {
          id: 's2',
          heading: 'Public Meetup Recommendations',
          body: 'Meet in high-traffic public locations such as shopping mall lobbies, bank premises, or verified logistics hubs during daylight hours.'
        }
      ]
    },
    {
      id: 'page_report',
      slug: '/report',
      title: 'Report a Product or Vendor',
      category: 'Need Help',
      subtitle: 'Help Us Keep Nigeria\'s Circular Marketplace Secure & Transparent',
      status: 'published',
      lastUpdated: 'Sep 17, 2026',
      sections: [
        {
          id: 's1',
          heading: 'Reporting Violations & Counterfeits',
          body: 'We enforce permanent bans against merchants listing counterfeit electronics, stolen gadgets, or attempting escrow bypass.'
        }
      ]
    },
    {
      id: 'page_about',
      slug: '/about',
      title: 'About Us & Circular Mission',
      category: 'About ReUseIt',
      subtitle: 'Pioneering Africa\'s Largest Sustainable Circular Commerce Ecosystem',
      status: 'published',
      lastUpdated: 'Sep 19, 2026',
      sections: [
        {
          id: 's1',
          heading: 'Our Origin & Vision',
          body: 'Founded in Lagos, Nigeria, ReUseIt empowers conscious consumers to extend gadget lifespans, reduce landfill e-waste, and trade thrift items safely.'
        },
        {
          id: 's2',
          heading: 'Tackling the Nigerian E-Waste Crisis',
          body: 'Over 150 metric tons of electronic and textile waste diverted from Lagos dumpsites in 2025-2026.'
        }
      ]
    },
    {
      id: 'page_impact',
      slug: '/impact',
      title: 'Carbon Impact & Tree Planting',
      category: 'About ReUseIt',
      subtitle: 'Every Pre-Owned Purchase Diverts Real CO2 and Plants Real Trees in Nigeria',
      status: 'published',
      lastUpdated: 'Sep 18, 2026',
      sections: [
        {
          id: 's1',
          heading: 'Life Cycle Assessment (LCA) Carbon Model',
          body: 'Purchasing a refurbished smartphone saves ~70kg CO2e, while pre-owned laptops divert ~280kg CO2e from manufacturing emissions.',
          bulletPoints: [
            'Smartphones & Tablets: ~70 kg CO2 saved',
            'Laptops & Desktops: ~280 kg CO2 saved',
            'Couches & Living Room Sets: ~90 kg CO2 saved'
          ]
        },
        {
          id: 's2',
          heading: 'The Green Nigeria Reforestation Fund',
          body: 'A share of each escrow fee directly funds indigenous tree planting across Northern Nigeria and Niger Delta mangrove restoration.'
        }
      ]
    },
    {
      id: 'page_leaderboard',
      slug: '/leaderboard',
      title: 'ReUseIt Eco Leaderboard',
      category: 'About ReUseIt',
      subtitle: 'Recognizing and Rewarding Nigeria\'s Top Circular Commerce Champions',
      status: 'published',
      lastUpdated: 'Sep 15, 2026',
      sections: [
        {
          id: 's1',
          heading: 'Eco Tiers & Privileges',
          body: 'Members level up from Seedling to Planet Champion by diverting carbon, unlocking lower escrow fees and store promotion badges.'
        }
      ]
    },
    {
      id: 'page_terms',
      slug: '/terms',
      title: 'Terms & Conditions of Service',
      category: 'Legal & Policies',
      subtitle: 'Official Contract Governing Use of ReUseIt Platform and Escrow Vault',
      status: 'published',
      lastUpdated: 'Sep 10, 2026',
      sections: [
        {
          id: 's1',
          heading: '1. Acceptance & Seller KYC Compliance',
          body: 'Vendors must submit valid government-issued identification or CAC business registration matching bank payout details.'
        },
        {
          id: 's2',
          heading: '2. Escrow Fees & Platform Deduction',
          body: '7.5% platform commission and statutory 7.5% VAT are deducted from vendor sales upon confirmed delivery.'
        }
      ]
    },
    {
      id: 'page_privacy',
      slug: '/privacy',
      title: 'Privacy Policy (NDPA Compliant)',
      category: 'Legal & Policies',
      subtitle: 'How ReUseIt Collects, Uses, and Protects Your Personal Data Under Nigerian Law',
      status: 'published',
      lastUpdated: 'Sep 12, 2026',
      sections: [
        {
          id: 's1',
          heading: 'Nigeria Data Protection Act (NDPA) 2023 Compliance',
          body: 'We adhere to the highest standard of data subject rights, consent gathering, and encryption standards.'
        }
      ]
    },
    {
      id: 'page_partner',
      slug: '/partner',
      title: 'Become a Verified Partner',
      category: 'About ReUseIt',
      subtitle: 'Partner With ReUseIt in Logistics, Inspection Hubs, and Circular Trade',
      status: 'published',
      lastUpdated: 'Sep 16, 2026',
      sections: [
        {
          id: 's1',
          heading: 'Partnership Categories',
          body: 'We invite logistics fleets, gadget diagnostics centers, and corporate recycling firms to join our verified partner network.'
        }
      ]
    }
  ]);

  // Modal states
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [activePage, setActivePage] = useState<DynamicPageItem | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [saveToast, setSaveToast] = useState(false);

  // Sync with PostgreSQL API & Firestore cms_pages on mount
  useEffect(() => {
    const loadPages = async () => {
      try {
        const apiArticles = await AdminApiClient.getArticles();
        if (apiArticles && apiArticles.length > 0) {
          const mappedFromApi: DynamicPageItem[] = apiArticles.map((a: any) => ({
            id: a.id,
            slug: a.slug.startsWith('/') ? a.slug : `/${a.slug}`,
            title: a.title,
            category: a.category === 'legal' ? 'Legal & Policies' : a.category === 'safety' ? 'About ReUseIt' : 'Need Help',
            subtitle: a.authorName || 'Platform Article',
            status: a.isPublished ? 'published' : 'draft',
            lastUpdated: new Date(a.updatedAt || a.createdAt).toLocaleDateString(),
            sections: [
              {
                id: 'sec_1',
                heading: a.title,
                body: a.content,
              }
            ],
          }));
          setPages(mappedFromApi);
          return;
        }
      } catch (e) {
        console.warn('API articles load fallback:', e);
      }

      try {
        const snap = await getDocs(collection(db, 'cms_pages'));
        if (!snap.empty) {
          const loaded: DynamicPageItem[] = [];
          snap.forEach(docSnap => {
            loaded.push({ id: docSnap.id, ...docSnap.data() } as DynamicPageItem);
          });
          setPages(loaded);
        }
      } catch (e) {
        console.warn('Using default CMS pages fallback:', e);
      }
    };
    loadPages();
  }, []);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editCategory, setEditCategory] = useState<'Need Help' | 'About ReUseIt' | 'Legal & Policies'>('Need Help');
  const [editSubtitle, setEditSubtitle] = useState('');
  const [editStatus, setEditStatus] = useState<'published' | 'draft'>('published');
  const [editSections, setEditSections] = useState<DynamicPageSection[]>([]);

  // Filtered pages
  const filteredPages = pages.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.slug.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCat = selectedCategory === 'All' || p.category === selectedCategory;
    const matchesStat = selectedStatus === 'All' || p.status === selectedStatus;
    return matchesSearch && matchesCat && matchesStat;
  });

  // Open Create Page Modal
  const handleOpenCreate = () => {
    setActivePage(null);
    setEditTitle('');
    setEditSlug('');
    setEditCategory('Need Help');
    setEditSubtitle('');
    setEditStatus('published');
    setEditSections([
      {
        id: `sec_${Date.now()}`,
        heading: '1. Overview',
        body: 'Provide the primary informational details for this section here.',
        callout: ''
      }
    ]);
    setEditorModalOpen(true);
  };

  // Open Edit Page Modal
  const handleOpenEdit = (page: DynamicPageItem) => {
    setActivePage(page);
    setEditTitle(page.title);
    setEditSlug(page.slug);
    setEditCategory(page.category);
    setEditSubtitle(page.subtitle);
    setEditStatus(page.status);
    setEditSections(JSON.parse(JSON.stringify(page.sections)));
    setEditorModalOpen(true);
  };

  // Open Preview Modal
  const handleOpenPreview = (page: DynamicPageItem) => {
    setActivePage(page);
    setPreviewModalOpen(true);
  };

  // Save Page
  const handleSavePage = async () => {
    if (!editTitle.trim()) return;
    const computedSlug = editSlug.trim().startsWith('/') 
      ? editSlug.trim() 
      : `/${editSlug.trim().toLowerCase().replace(/\s+/g, '-')}`;

    const pageId = activePage ? activePage.id : `page_${Date.now()}`;
    const pageData: DynamicPageItem = {
      id: pageId,
      title: editTitle,
      slug: computedSlug,
      category: editCategory,
      subtitle: editSubtitle,
      status: editStatus,
      lastUpdated: 'Just now',
      sections: editSections
    };

    if (activePage) {
      // Update existing
      setPages(prev => prev.map(p => p.id === activePage.id ? pageData : p));
    } else {
      // Create new
      setPages(prev => [pageData, ...prev]);
    }

    // Live Backend Persistence in PostgreSQL
    try {
      const cleanSlug = editSlug.trim().replace(/^\//, '');
      const fullContent = editSections.map(s => `${s.heading}\n\n${s.body}`).join('\n\n');
      const catKey = editCategory === 'Legal & Policies' ? 'legal' : editCategory === 'About ReUseIt' ? 'safety' : 'help';
      await AdminApiClient.saveArticle({
        id: activePage ? activePage.id : undefined,
        slug: cleanSlug,
        title: editTitle.trim(),
        category: catKey,
        content: fullContent,
        authorName: editSubtitle.trim() || 'Platform Operations',
        isPublished: editStatus === 'published',
      });
    } catch (apiErr: any) {
      console.warn('PostgreSQL API article save error:', apiErr);
    }

    try {
      await setDoc(doc(db, 'cms_pages', pageId), {
        ...pageData,
        updatedAt: serverTimestamp(),
      });
      await setDoc(doc(db, 'cms_metadata', 'global'), {
        lastSyncedAt: serverTimestamp(),
        lastUpdatedPage: pageId,
        updatedBy: 'Admin User',
      }, { merge: true });
    } catch (e) {
      console.warn('Firestore CMS save error:', e);
    }

    setEditorModalOpen(false);
    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 3000);
  };

  // Delete Page
  const handleDeletePage = async (id: string) => {
    setPages(prev => prev.filter(p => p.id !== id));
    try {
      await AdminApiClient.deleteArticle(id);
    } catch (apiErr: any) {
      console.warn('PostgreSQL API article delete error:', apiErr);
    }

    try {
      await deleteDoc(doc(db, 'cms_pages', id));
      await setDoc(doc(db, 'cms_metadata', 'global'), {
        lastSyncedAt: serverTimestamp(),
        action: 'deleted',
        deletedPageId: id,
      }, { merge: true });
    } catch (e) {
      console.warn('Firestore CMS delete error:', e);
    }
    setDeleteConfirmId(null);
  };

  // Section editor helpers
  const handleAddSection = () => {
    const newSec: DynamicPageSection = {
      id: `sec_${Date.now()}`,
      heading: 'New Section Heading',
      body: 'Add your informational body content here...',
    };
    setEditSections([...editSections, newSec]);
  };

  const handleUpdateSection = (id: string, field: 'heading' | 'body' | 'callout', value: string) => {
    setEditSections(prev => prev.map(s => {
      if (s.id === id) {
        return { ...s, [field]: value };
      }
      return s;
    }));
  };

  const handleDeleteSection = (id: string) => {
    if (editSections.length <= 1) return;
    setEditSections(prev => prev.filter(s => s.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {saveToast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-brand-500 text-forest-void font-bold shadow-glow-lg animate-fade-in">
          <CheckCircle className="w-5 h-5" />
          <span>Page changes saved & synchronized with ReUseIt marketplace!</span>
        </div>
      )}

      {/* Header & Action Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-white light:text-[#0C1D18]">
              Dynamic Pages & Policy CMS
            </h2>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-brand-500/20 text-brand-400 border border-brand-500/30">
              {pages.length} Pages Live
            </span>
          </div>
          <p className="text-xs text-[#90cbb7] light:text-[#475569] mt-1">
            Create, edit, preview, and delete information pages linked in the marketplace footer and mobile drawer.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all"
        >
          <Plus className="w-4 h-4" />
          <span>Create New Page</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 rounded-2xl bg-[#152a23] dark:bg-[#152a23] light:bg-white border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC]">
        {/* Search */}
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-[#90cbb7] absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title or slug..."
            className="w-full pl-10 pr-4 py-2 rounded-xl text-xs bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F4F9F6] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] text-white light:text-[#0C1D18] focus:outline-none focus:border-brand-500"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2.5 w-full sm:w-auto overflow-x-auto">
          {/* Category filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 rounded-xl text-xs bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F4F9F6] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] text-white light:text-[#0C1D18] focus:outline-none"
          >
            <option value="All">All Categories</option>
            <option value="Need Help">Need Help</option>
            <option value="About ReUseIt">About ReUseIt</option>
            <option value="Legal & Policies">Legal & Policies</option>
          </select>

          {/* Status filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 rounded-xl text-xs bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F4F9F6] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] text-white light:text-[#0C1D18] focus:outline-none"
          >
            <option value="All">All Statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Pages Table */}
      <div className="rounded-3xl border overflow-hidden bg-[#152a23] dark:bg-[#152a23] light:bg-white border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC]">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] bg-[#0c1d18]/50 dark:bg-[#0c1d18]/50 light:bg-[#F4F9F6] text-[#90cbb7] light:text-[#475569]">
                <th className="px-6 py-3.5 font-bold">Page Title & Slug</th>
                <th className="px-6 py-3.5 font-bold">Category</th>
                <th className="px-6 py-3.5 font-bold">Sections</th>
                <th className="px-6 py-3.5 font-bold">Status</th>
                <th className="px-6 py-3.5 font-bold">Last Updated</th>
                <th className="px-6 py-3.5 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#22493c]/50 dark:divide-[#22493c]/50 light:divide-[#D5E4DC]">
              {filteredPages.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-[#90cbb7]">
                    No dynamic pages found matching your search.
                  </td>
                </tr>
              ) : (
                filteredPages.map((page) => (
                  <tr key={page.id} className="hover:bg-[#18342b]/40 dark:hover:bg-[#18342b]/40 light:hover:bg-[#F9FCFA] transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-bold text-white light:text-[#0C1D18] text-sm">
                        {page.title}
                      </div>
                      <div className="text-[#90cbb7] light:text-[#64748B] font-mono text-[11px] mt-0.5">
                        {page.slug}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold inline-flex items-center gap-1
                        ${page.category === 'Need Help' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
                          page.category === 'About ReUseIt' ? 'bg-brand-500/15 text-brand-400 border border-brand-500/30' :
                          'bg-blue-500/15 text-blue-400 border border-blue-500/30'}
                      `}>
                        {page.category}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-white light:text-[#0C1D18]">
                      {page.sections.length} content blocks
                    </td>

                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold
                        ${page.status === 'published' ? 'bg-brand-500/20 text-brand-400' : 'bg-slate-500/20 text-slate-400'}
                      `}>
                        {page.status.toUpperCase()}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-[#90cbb7] light:text-[#64748B]">
                      {page.lastUpdated}
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Live Preview Button */}
                        <button
                          onClick={() => handleOpenPreview(page)}
                          className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] transition-colors"
                          title="Preview Page"
                        >
                          <Eye className="w-4 h-4 text-brand-400" />
                        </button>

                        {/* Edit Button */}
                        <button
                          onClick={() => handleOpenEdit(page)}
                          className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] transition-colors"
                          title="Edit Page"
                        >
                          <Edit3 className="w-4 h-4 text-amber-400" />
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => setDeleteConfirmId(page.id)}
                          className="p-1.5 rounded-lg text-[#90cbb7] hover:text-rose-400 hover:bg-[#22493c] transition-colors"
                          title="Delete Page"
                        >
                          <Trash2 className="w-4 h-4 text-rose-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ========================================================= */}
      {/* MODAL 1: CREATE / EDIT PAGE MODAL                         */}
      {/* ========================================================= */}
      {editorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl bg-[#10231D] dark:bg-[#10231D] light:bg-white border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] p-6 text-white light:text-[#0C1D18] shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-4 border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC]">
              <div className="flex items-center gap-2.5">
                <FileText className="w-5 h-5 text-brand-400" />
                <h3 className="text-lg font-bold">
                  {activePage ? `Edit: ${activePage.title}` : 'Create New Dynamic Page'}
                </h3>
              </div>
              <button
                onClick={() => setEditorModalOpen(false)}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-[#90cbb7]" />
              </button>
            </div>

            {/* Form Content */}
            <div className="space-y-4 py-5">
              {/* Row 1: Title and Slug */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                    Page Title *
                  </label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => {
                      setEditTitle(e.target.value);
                      if (!activePage) {
                        setEditSlug(`/${e.target.value.toLowerCase().replace(/\s+/g, '-')}`);
                      }
                    }}
                    placeholder="e.g. Tree Planting & Carbon Impact"
                    className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F4F9F6] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] text-white light:text-[#0C1D18] focus:outline-none focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                    URL Slug *
                  </label>
                  <input
                    type="text"
                    value={editSlug}
                    onChange={(e) => setEditSlug(e.target.value)}
                    placeholder="e.g. /tree-planting"
                    className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F4F9F6] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] font-mono text-white light:text-[#0C1D18] focus:outline-none focus:border-brand-500"
                  />
                </div>
              </div>

              {/* Row 2: Category and Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                    Category *
                  </label>
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value as any)}
                    className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F4F9F6] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] text-white light:text-[#0C1D18] focus:outline-none"
                  >
                    <option value="Need Help">Need Help</option>
                    <option value="About ReUseIt">About ReUseIt</option>
                    <option value="Legal & Policies">Legal & Policies</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                    Publication Status
                  </label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value as any)}
                    className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F4F9F6] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] text-white light:text-[#0C1D18] focus:outline-none"
                  >
                    <option value="published">Published (Live immediately)</option>
                    <option value="draft">Draft (Hidden from users)</option>
                  </select>
                </div>
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                  Subtitle / Hero Tagline
                </label>
                <input
                  type="text"
                  value={editSubtitle}
                  onChange={(e) => setEditSubtitle(e.target.value)}
                  placeholder="e.g. Empowering Nigerians to trade sustainably with 100% escrow security"
                  className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F4F9F6] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] text-white light:text-[#0C1D18] focus:outline-none focus:border-brand-500"
                />
              </div>

              {/* Dynamic Section Blocks Editor */}
              <div className="pt-3 border-t border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC]">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold text-brand-400 uppercase tracking-wider">
                    Content Sections ({editSections.length})
                  </h4>
                  <button
                    type="button"
                    onClick={handleAddSection}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#152a23] hover:bg-[#22493c] border border-brand-500/30 text-brand-400 transition-all"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Section</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {editSections.map((sec, idx) => (
                    <div
                      key={sec.id}
                      className="p-4 rounded-2xl bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F8FAF9] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-[#90cbb7]">
                          Section #{idx + 1}
                        </span>
                        {editSections.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleDeleteSection(sec.id)}
                            className="text-rose-400 hover:text-rose-300 text-xs font-semibold"
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <input
                        type="text"
                        value={sec.heading}
                        onChange={(e) => handleUpdateSection(sec.id, 'heading', e.target.value)}
                        placeholder="Section Heading"
                        className="w-full px-3 py-2 rounded-lg text-xs bg-[#152a23] dark:bg-[#152a23] light:bg-white border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] text-white light:text-[#0C1D18] focus:outline-none focus:border-brand-500 font-bold"
                      />

                      <textarea
                        rows={3}
                        value={sec.body}
                        onChange={(e) => handleUpdateSection(sec.id, 'body', e.target.value)}
                        placeholder="Section body explanation..."
                        className="w-full px-3 py-2 rounded-lg text-xs bg-[#152a23] dark:bg-[#152a23] light:bg-white border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] text-white light:text-[#0C1D18] focus:outline-none focus:border-brand-500"
                      />

                      <input
                        type="text"
                        value={sec.callout || ''}
                        onChange={(e) => handleUpdateSection(sec.id, 'callout', e.target.value)}
                        placeholder="Optional highlighted callout note..."
                        className="w-full px-3 py-1.5 rounded-lg text-xs bg-[#152a23]/60 dark:bg-[#152a23]/60 light:bg-white border border-brand-500/20 text-brand-300 light:text-[#0C1D18] focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC]">
              <button
                type="button"
                onClick={() => setEditorModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-[#90cbb7] hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePage}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm"
              >
                <Save className="w-4 h-4" />
                <span>Save & Publish</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 2: LIVE RESPONSIVE PREVIEW MODAL                    */}
      {/* ========================================================= */}
      {previewModalOpen && activePage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
          <div className="w-full max-w-4xl max-h-[92vh] flex flex-col rounded-3xl bg-[#0c1d18] dark:bg-[#0c1d18] light:bg-[#F0FAF5] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] overflow-hidden shadow-2xl">
            {/* Preview Toolbar */}
            <div className="flex items-center justify-between px-6 py-3.5 bg-[#10231D] dark:bg-[#10231D] light:bg-white border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC]">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-brand-400" />
                <span className="text-xs font-bold text-white light:text-[#0C1D18]">
                  Live Device Preview: {activePage.title}
                </span>
              </div>

              {/* Device Selector */}
              <div className="flex items-center gap-2 bg-[#0c1d18] p-1 rounded-full border border-[#22493c]">
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all
                    ${previewDevice === 'desktop' ? 'bg-brand-500 text-forest-void' : 'text-[#90cbb7] hover:text-white'}
                  `}
                >
                  <Monitor className="w-3.5 h-3.5" />
                  <span>Desktop View</span>
                </button>
                <button
                  onClick={() => setPreviewDevice('mobile')}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all
                    ${previewDevice === 'mobile' ? 'bg-brand-500 text-forest-void' : 'text-[#90cbb7] hover:text-white'}
                  `}
                >
                  <Smartphone className="w-3.5 h-3.5" />
                  <span>Mobile View</span>
                </button>
              </div>

              <button
                onClick={() => setPreviewModalOpen(false)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <X className="w-5 h-5 text-[#90cbb7]" />
              </button>
            </div>

            {/* Viewport Frame */}
            <div className="flex-1 overflow-y-auto p-6 flex justify-center bg-[#07100D]">
              <div
                className={`transition-all duration-300 rounded-2xl overflow-hidden border border-[#22493c] bg-[#0c1d18] text-white p-6 shadow-xl
                  ${previewDevice === 'desktop' ? 'w-full max-w-3xl' : 'w-[375px]'}
                `}
              >
                {/* Mock Header */}
                <div className="pb-4 border-b border-[#22493c] mb-6 flex items-center justify-between text-xs text-[#90cbb7]">
                  <span className="font-bold text-brand-400">ReUseIt MARKETPLACE</span>
                  <span>{activePage.category}</span>
                </div>

                {/* Breadcrumbs */}
                <div className="text-[11px] text-[#90cbb7] mb-4 font-mono">
                  Home / {activePage.category} / <span className="text-brand-400 font-bold">{activePage.title}</span>
                </div>

                {/* Hero Title */}
                <div className="p-5 rounded-2xl bg-[#152a23] border border-[#22493c] mb-6">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-brand-500/20 text-brand-400 uppercase">
                    {activePage.category}
                  </span>
                  <h1 className="text-2xl font-bold mt-2">{activePage.title}</h1>
                  <p className="text-xs text-[#90cbb7] mt-1">{activePage.subtitle}</p>
                </div>

                {/* Sections */}
                <div className="space-y-4">
                  {activePage.sections.map((sec) => (
                    <div key={sec.id} className="p-4 rounded-xl bg-[#152a23] border border-[#22493c]">
                      <h2 className="text-sm font-bold text-white mb-2">{sec.heading}</h2>
                      <p className="text-xs text-[#90cbb7] leading-relaxed">{sec.body}</p>
                      {sec.bulletPoints && (
                        <ul className="mt-2.5 space-y-1 text-xs text-white/90">
                          {sec.bulletPoints.map((bp, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-brand-400 mt-1">•</span>
                              <span>{bp}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {sec.callout && (
                        <div className="mt-3 p-3 rounded-lg bg-brand-500/10 border border-brand-500/30 text-xs font-semibold text-brand-400">
                          {sec.callout}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Mock Footer */}
                <div className="mt-8 pt-4 border-t border-[#22493c] text-center text-[10px] text-[#90cbb7]">
                  © 2026 ReUseIt Nigeria Limited • 100% Escrow Protection Vault
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 3: DELETE CONFIRMATION DIALOG                       */}
      {/* ========================================================= */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md rounded-2xl bg-[#10231D] border border-rose-500/30 p-6 text-white shadow-2xl">
            <div className="flex items-center gap-3 text-rose-400 mb-3">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-base font-bold">Delete Dynamic Page</h3>
            </div>
            <p className="text-xs text-[#90cbb7] leading-relaxed">
              Are you sure you want to permanently delete this page? Any footer and mobile drawer links pointing to this slug will need to be redirected.
            </p>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-[#90cbb7] hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePage(deleteConfirmId)}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-500"
              >
                Delete Page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
